import featureConfig from '@/featureConfig';
import { useIsAnalysisFailed, useIsAnalysisInProgress, useIsLCAAndTEASucceeded } from '@/stores/analysisStatusStore';
import { useIsBalanced, caseActions, useAnalysisTypes, useAnalysisStatus } from '@/stores/caseStore';
import { useParentPathwayId } from '@/stores/pathwayStore';
import { PlayIcon } from '@heroicons/react/24/outline';
import { ChevronDownIcon } from '@heroicons/react/24/solid';
import { useState } from 'react';

const AnalysisButton = () => {
  const isBalanced = useIsBalanced();
  const isAnalysisFailed = useIsAnalysisFailed();
  const isLCAAndTEASucceeded = useIsLCAAndTEASucceeded();
  const isAnalysisInProgress = useIsAnalysisInProgress();
  const parentPathwayId = useParentPathwayId();

  const analysisTypes = useAnalysisTypes();
  const hasUncertaintyAnalysis = analysisTypes.includes('mc');

  const analysisStatus = useAnalysisStatus();

  const [selectedSensitivity, setSelectedSensitivity] = useState(analysisStatus?.sensitivity ?? false);
  const [selectedMonteCarlo, setSelectedMonteCarlo] = useState(analysisStatus?.mc ?? false);

  const analyzePathway = async types => {
    caseActions.analyzeCase({ analysis_types: types });
  };

  const runAnalysis = () => {
    const types = ['lca', 'tea'];

    if (selectedSensitivity) {
      types.push('sensitivity');
    }

    if (selectedMonteCarlo && hasUncertaintyAnalysis) {
      types.push('mc');
    }

    analyzePathway(types);
  };

  if (parentPathwayId || isAnalysisInProgress) {
    return null;
  }

  if (!isBalanced) {
    return (
      <button className="btn btn-sm btn-disabled">
        <PlayIcon className="w-5 h-5" />
        Analyze Pathway
      </button>
    );
  }

  if (isAnalysisFailed) {
    return <button className="btn btn-sm btn-error">Analysis failed</button>;
  }

  return (
    <div className="join">
      <button
        type="button"
        onClick={runAnalysis}
        className={`btn btn-sm join-item ${isLCAAndTEASucceeded ? 'btn-secondary' : 'btn-accent'}`}
      >
        <PlayIcon className="w-5 h-5" />
        Analyze Pathway
      </button>
      <div className="dropdown dropdown-top dropdown-end border-l">
        <div
          tabIndex={0}
          role="button"
          className={`btn btn-sm join-item ${isLCAAndTEASucceeded ? 'btn-secondary' : 'btn-accent'}`}
        >
          <ChevronDownIcon className="w-4 h-5" />
        </div>
        <div
          tabIndex={0}
          className="dropdown-content z-[1] menu shadow-md bg-base-100 w-80 border border-gray-200 mb-1 rounded p-0"
        >
          <ul className="p-0 m-0">
            <li className="border-b border-gray-200">
              <label className="flex items-center gap-3 p-4 hover:bg-gray-50 cursor-default rounded-t">
                <input type="checkbox" checked={true} className="checkbox checkbox-sm" />
                <div className="flex flex-col">
                  <span className="font-bold">LCA + TEA</span>
                </div>
              </label>
            </li>
            <li className="border-b border-gray-200 last:border-b-0">
              <label className="flex items-center gap-3 p-4 hover:bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedSensitivity}
                  onChange={e => setSelectedSensitivity(e.target.checked)}
                  className="checkbox checkbox-sm"
                />
                <div className="flex flex-col">
                  <span className="font-bold">Sensitivity Analysis</span>
                </div>
              </label>
            </li>
            {featureConfig.enableMonteCarloSimulation && (
              <li>
                <label
                  className={`flex items-center gap-3 p-4 rounded-b ${
                    hasUncertaintyAnalysis
                      ? 'hover:bg-gray-50 cursor-pointer'
                      : 'opacity-50 cursor-not-allowed bg-gray-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedMonteCarlo}
                    onChange={e => hasUncertaintyAnalysis && setSelectedMonteCarlo(e.target.checked)}
                    disabled={!hasUncertaintyAnalysis}
                    className="checkbox checkbox-sm"
                  />
                  <div className="flex flex-col">
                    <span className="font-bold">Monte Carlo Simulation</span>
                    <span className={`text-xs ${hasUncertaintyAnalysis ? 'text-gray-500' : 'text-gray-400'}`}>
                      5-15 minutes
                    </span>
                  </div>
                </label>
              </li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AnalysisButton;
